import { IAppContext } from '@byted-cg/linkchat-core';
import { Conversation, FileInfo } from '@byted/im-web-sdk';
import { I18n } from '../utils/lang';
import store from '../store/cache';

import { MESSAGE_ALL_TYPE } from '../constants';
import { DIALOG_STATUS, SEND_TYPE } from '../constants/status';
import { DeleyInfo, ImcloudInfo, Info } from '../constants/metrics';
import { monitor, logger } from '../lib/metrics';
import Channel from '../core/ws';
import { CombineMessageType, IFileMessage, ITextMessage } from './type';

let reLinkDialogNums = 0; // Number of session reconnections, up to 3 reconnections
let createDialogIng = false;

export const ROBOT_SENDER = 1000001;
export interface IMessageContent {
  type?: number; //Message type
  content?: string; // When the file type is FileList
  ext?: Record<string, string>;
  noCreateDialogOnEndStatus?: boolean; //No need to verify session state to create a session
  referencedMessageId?: string; // Referred message ID
}

export interface IFileMessageContent {
  type: number;
  content: FileInfo;
  ext?: Record<string, string>;
  referencedMessageId?: string; // Referred message ID
  noCreateDialogOnEndStatus?: boolean; //No need to verify session state to create a session
}

export interface IJarvisCardInfo {
  appCode: string;
  data: string;
  dataFromCache: boolean;
  eventCode: string;
  triggerType: string;
}

/**
 * @description Get the user of the current session
 * @param conversations
 * @returns string
 */
const getParticipants = (conversations: Conversation) => {
  return (
    conversations.firstPageParticipant?.participants
      ?.map?.((participant) => participant.user_id)
      .filter(Boolean)
      .join(',') || ''
  );
};

// Here we deal with the own properties of Linkchat messages.
const getExt = (
  appContext: IAppContext<CombineMessageType>,
  customInfo?: any,
) => {
  const { dialog, dialogInfo } = appContext;
  let dialogCoreInfo: {
    [key: string]: string;
  } = {};
  if (customInfo || dialogInfo) {
    dialogCoreInfo = customInfo || dialogInfo;
  } else {
    dialogCoreInfo = {
      ...dialog.ext,
      conversationTitle: store.get('userName'),
      id: dialog.ext?.dialogId,
      dialogId: dialog.ext?.dialogId,
      businessLineId: dialog.ext?.businessLineId,
      type: dialog.ext?.type,
    };
  }

  const ext: Record<string, string> = {
    senderName: dialogCoreInfo?.conversationTitle || '',
    conversationTitle: dialogCoreInfo?.conversationTitle || '',
    businessLineId: String(dialogCoreInfo?.businessLineId),
    dialogId: String(dialogCoreInfo?.id),
    participants: getParticipants(dialog),
    senderType: SEND_TYPE.USER, // O is the message sent by the user
    dialogScene: '1',
    dialogType:
      dialogCoreInfo?.type === undefined ? '' : String(dialogCoreInfo?.type),
  };
  return ext;
};

// Create reference reply message
async function createReferenceMessage<T extends IFileMessage | ITextMessage>(
  message: T,
  getMessageByServerId: any,
) {
  // Create reference reply message
  const { referencedMessageId, ext, ...others } = message;
  if (referencedMessageId) {
    const referenceMessage = await getMessageByServerId({
      conversation: message.conversation,
      serverMessageId: referencedMessageId,
    });

    if (referenceMessage) {
      // Save so much information, mainly because the cdn has expired and the url needs to be refreshed.
      const referencedMessage = JSON.stringify({
        serverId: referenceMessage.serverId,
        type: referenceMessage.type,
        senderName: referenceMessage.ext.senderName,
        senderType: referenceMessage.ext.senderType,
        createdTime: referenceMessage.createdAt.getTime(),
        content: referenceMessage.content,
      });
      return {
        ...others,
        referenceMessage,
        ext: {
          ...ext,
          referencedMessage,
        },
      } as unknown as T;
    }
  }
  return {
    ...others,
    ext,
  } as unknown as T;
}

const sendInClosedDialog = async (
  appContext: IAppContext<CombineMessageType, Channel>,
) => {
  // 会话状态为结束并且reLinkDialogNums不能为0（阻止多次发起）
  if (
    +appContext.dialog?.ext?.status !== DIALOG_STATUS.ENDING ||
    createDialogIng
  ) {
    return false;
  }
  if (reLinkDialogNums >= 3) {
    throw new Error(
      I18n.t(
        'the_session_has_ended__reconnection_failed__click_try_again',
        {},
        '会话已结束，重新连接失败，点击再次尝试',
      ),
    );
  }
  createDialogIng = true;
  try {
    reLinkDialogNums += 1;
    const { dialog = {} } = await appContext.utils?.services?.createDialog();
    reLinkDialogNums = 0;
    createDialogIng = false;
    return dialog;
  } catch (e) {
    createDialogIng = false;
    logger.error(Info.CreateDialogError, {
      error: e,
    });
    throw e;
  }
};

/**
 * @description Send file message
 * @param appContext
 * @returns
 */
const createSendFile =
  (appContext: IAppContext<CombineMessageType, Channel>) =>
  async (message: IFileMessageContent) => {
    try {
      const dur = monitor.emitDuration(DeleyInfo.SendFileMessage);
      const { id } = appContext.channel?.conversationInfo || {};
      if (!id || !appContext.channel) {
        throw new TypeError(
          I18n.t('non_existent_session_id', {}, '不存在的会话ID'),
        );
      }
      const {
        type,
        content,
        ext: sendExt = { originWidth: '', originHeight: '' },
        referencedMessageId,
      } = message;
      // If the session state has ended, the session needs to be recreated

      let dialogCheckInfo = false;
      if (!message.noCreateDialogOnEndStatus) {
        dialogCheckInfo = await sendInClosedDialog(appContext);
      }
      // Get ext
      const fileInfo: FileInfo = content;

      const ext = { ...getExt(appContext, dialogCheckInfo), ...sendExt };

      const messageContent = {
        conversation: appContext.channel?.conversationInfo as Conversation,
        type,
        fileInfo,
        referencedMessageId:
          referencedMessageId || appContext?.replyMessage?.serverId,
        ext,
      };

      const messageInstance = await createReferenceMessage(
        messageContent,
        appContext.channel?.instance.getMessageByServerId,
      );

      const msg = await appContext.channel?.instance.createFileMessage(
        messageInstance,
      );
      // Send different message formats according to different types: pictures/videos/files
      const result =
        msg &&
        (await appContext.channel?.instance?.sendMessage({
          message: msg,
        }));
      if (appContext?.replyMessage?.serverId) {
        appContext.replyMessage = {};
      }
      dur();
      return result;
    } catch (e) {
      logger.error(ImcloudInfo.SendFileMessageError, {
        error: e,
      });
      throw e;
    }
  };

/**
 * @description Send text message
 * @param appContext
 * @returns
 */
const createSendMessage =
  (appContext: IAppContext<CombineMessageType, Channel>) =>
  async ({
    type = MESSAGE_ALL_TYPE.MESSAGE_TYPE_TEXT,
    content,
    ext = {},
    referencedMessageId,
    noCreateDialogOnEndStatus,
  }: IMessageContent) => {
    try {
      if (!content) return;
      const dur = monitor.emitDuration(DeleyInfo.SendMessage);
      const { id } = appContext.channel?.conversationInfo || {};

      if (!id || !appContext.channel) {
        throw new TypeError(
          I18n.t('non_existent_session_id', {}, '不存在的会话ID'),
        );
      }
      // If the session state has ended, the session needs to be recreated
      let dialogCheckInfo = false;
      if (!noCreateDialogOnEndStatus) {
        dialogCheckInfo = await sendInClosedDialog(appContext);
      }
      // Get ext
      const filterExt = { ...getExt(appContext, dialogCheckInfo), ...ext };

      const messageContent = {
        conversation: appContext.channel.conversationInfo as Conversation,
        type,
        content,
        ext: filterExt,
        referencedMessageId:
          referencedMessageId || appContext?.replyMessage?.serverId,
      };

      const messageInstance = await createReferenceMessage(
        messageContent,
        appContext.channel?.instance.getMessageByServerId,
      );
      const msg = await appContext.channel?.instance.createMessage(
        messageInstance,
      );
      // Send different message formats according to different types: file/picture/video, normal text
      const result =
        msg &&
        (await appContext.channel?.instance?.sendMessage({
          message: msg,
        }));
      if (appContext?.replyMessage?.serverId) {
        appContext.replyMessage = {};
      }
      dur();
      return result;
    } catch (e) {
      logger.error(ImcloudInfo.SendMessageError, {
        error: e,
      });
      throw e;
    }
  };

export { createSendFile, createSendMessage };
